import type { PartnerProfilAendern } from '@nac2s/shared'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { Input } from '@/components/base/input/input'
import { Hinweis } from '@/teile/Hinweis'
import { Knopf } from '@/teile/Knopf'
import { Fehler } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { ladeProfil, speichereProfil } from '../api/partner.js'
import { Anbindung } from '../teile/Anbindung.js'

/**
 * Unternehmens- und Supportdaten des Partners.
 *
 * Zwei Blöcke, ein Formular: links das Unternehmen, rechts der Support. Was
 * hier steht, sehen die Kunden in der Fußzeile ihrer Anwendung — deshalb
 * sagt die Seite das auch, statt es dem Partner zu überlassen, es zu ahnen.
 */
const LEER: PartnerProfilAendern = {
  unternehmen: { name: '', strasse: '', plz: '', ort: '', telefon: '', email: '', web: '' },
  support: { telefon: '', email: '' },
}

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'bereit'; aktualisiertAm: string | null }

export function Unternehmen() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [entwurf, setEntwurf] = useState<PartnerProfilAendern>(LEER)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'erfolg' | 'fehler'; text: string } | null>(null)

  const laden = useCallback(async () => {
    try {
      const profil = await ladeProfil()
      setEntwurf({ unternehmen: profil.unternehmen, support: profil.support })
      setZustand({ status: 'bereit', aktualisiertAm: profil.aktualisiertAm })
    } catch (fehler) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
      })
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  function unternehmen<K extends keyof PartnerProfilAendern['unternehmen']>(feld: K) {
    return (wert: string) =>
      setEntwurf((e) => ({ ...e, unternehmen: { ...e.unternehmen, [feld]: wert } }))
  }
  function support<K extends keyof PartnerProfilAendern['support']>(feld: K) {
    return (wert: string) => setEntwurf((e) => ({ ...e, support: { ...e.support, [feld]: wert } }))
  }

  async function speichern(event: FormEvent) {
    event.preventDefault()
    setLaeuft(true)
    setMeldung(null)
    try {
      const profil = await speichereProfil(entwurf)
      setEntwurf({ unternehmen: profil.unternehmen, support: profil.support })
      setZustand({ status: 'bereit', aktualisiertAm: profil.aktualisiertAm })
      setMeldung({ art: 'erfolg', text: 'Gespeichert. Ihre Kunden sehen die Daten ab sofort.' })
    } catch (fehler) {
      setMeldung({
        art: 'fehler',
        text: fehler instanceof ApiError ? fehler.message : 'Speichern fehlgeschlagen.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div>
      <div className="relative overflow-hidden px-6 pt-6 pb-[18px]">
        <div
          aria-hidden="true"
          className="schimmer pointer-events-none absolute -top-[13rem] -right-[6rem] size-[25rem]"
        />
        <div className="relative">
          <p className="text-[13px] text-quaternary">Ihr Haus</p>
          <h1 className="mt-4 font-display text-[26px] leading-[1.1] font-extrabold tracking-[-0.025em] text-primary">
            Unternehmen
          </h1>
          <p className="mt-1 text-sm text-tertiary">
            Ihre Firmendaten und der Support-Kontakt. In der Fußzeile jeder Kundenanwendung steht:
            „Sie brauchen Hilfe? Dann hier melden:" — Firma, Telefon, E-Mail.
          </p>
        </div>
      </div>

      <div className="px-6 pb-8">
        {zustand.status === 'laedt' ? (
          <p role="status" className="text-sm text-tertiary">
            Wird geladen …
          </p>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : (
          <form onSubmit={speichern} className="flex flex-col gap-6">
            <div className="flex flex-wrap gap-x-10 gap-y-6">
              <section className="flex w-[22rem] max-w-full flex-col gap-3.5">
                <h2 className="font-display text-base font-bold text-primary">Unternehmensdaten</h2>
                <Input
                  label="Firma"
                  size="sm"
                  value={entwurf.unternehmen.name}
                  onChange={unternehmen('name')}
                />
                <Input
                  label="Straße und Hausnummer"
                  size="sm"
                  value={entwurf.unternehmen.strasse}
                  onChange={unternehmen('strasse')}
                />
                <div className="grid grid-cols-[7rem_1fr] gap-3">
                  <Input
                    label="PLZ"
                    size="sm"
                    value={entwurf.unternehmen.plz}
                    onChange={unternehmen('plz')}
                  />
                  <Input
                    label="Ort"
                    size="sm"
                    value={entwurf.unternehmen.ort}
                    onChange={unternehmen('ort')}
                  />
                </div>
                <Input
                  label="Telefon"
                  size="sm"
                  type="tel"
                  value={entwurf.unternehmen.telefon}
                  onChange={unternehmen('telefon')}
                />
                <Input
                  label="E-Mail"
                  size="sm"
                  type="email"
                  value={entwurf.unternehmen.email}
                  onChange={unternehmen('email')}
                />
                <Input
                  label="Website"
                  size="sm"
                  type="url"
                  placeholder="https://"
                  value={entwurf.unternehmen.web}
                  onChange={unternehmen('web')}
                />
              </section>

              <section className="flex w-[22rem] max-w-full flex-col gap-3.5">
                <h2 className="font-display text-base font-bold text-primary">Support-Kontakt</h2>
                <p className="-mt-1 text-[13px] text-tertiary">
                  Bleibt ein Feld leer, gilt der Wert aus den Unternehmensdaten.
                </p>
                <Input
                  label="Telefon"
                  size="sm"
                  type="tel"
                  value={entwurf.support.telefon}
                  onChange={support('telefon')}
                />
                <Input
                  label="E-Mail"
                  size="sm"
                  type="email"
                  value={entwurf.support.email}
                  onChange={support('email')}
                />
              </section>
            </div>

            {meldung ? <Hinweis art={meldung.art}>{meldung.text}</Hinweis> : null}

            <div className="flex items-center gap-4">
              <Knopf type="submit" stark disabled={laeuft}>
                {laeuft ? 'Wird gespeichert …' : 'Speichern'}
              </Knopf>
              {zustand.aktualisiertAm ? (
                <span className="text-[12.5px] text-quaternary">
                  Zuletzt gespeichert {new Date(zustand.aktualisiertAm).toLocaleString('de-DE')}
                </span>
              ) : null}
            </div>
          </form>
        )}
        <div className="mt-10">
          <Anbindung />
        </div>
      </div>
    </div>
  )
}
